import React from "react";
import DOMPurify from "dompurify";

// Sanitize with a very restrictive allowlist — only inline formatting.
const SANITIZE_CONFIG = {
    ALLOWED_TAGS: ["strong", "em", "b", "i"],
    ALLOWED_ATTR: ["class"],
};

const sanitize = (html) => DOMPurify.sanitize(html, SANITIZE_CONFIG);

const boldStrong = (text) =>
    text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
const boldStrongBrand = (text) =>
    text.replace(/\*\*(.+?)\*\*/g, "<strong class='text-brand'>$1</strong>");

/**
 * Render a small subset of Markdown (headings, lists, bold) safely.
 * All raw HTML is sanitized with DOMPurify before being rendered.
 * Uses content-derived keys (with occurrence index) so React reconciles
 * correctly when lines are reordered/edited.
 */
export default function FormattedMarkdown({ text }) {
    if (!text) return null;
    const lines = text.split("\n");
    const seen = new Map();
    const stableKey = (raw) => {
        const base = raw.slice(0, 80);
        const n = (seen.get(base) || 0) + 1;
        seen.set(base, n);
        return `${base}::${n}`;
    };

    return (
        <div className="space-y-2">
            {lines.map((line) => {
                const key = stableKey(line);
                if (line.startsWith("# ")) {
                    return (
                        <h2 key={key} className="font-display text-xl font-medium text-brand mt-4 first:mt-0">
                            {line.slice(2)}
                        </h2>
                    );
                }
                if (line.startsWith("## ")) {
                    return (
                        <h3 key={key} className="font-display text-lg font-medium text-brand mt-3">
                            {line.slice(3)}
                        </h3>
                    );
                }
                if (line.startsWith("### ")) {
                    return (
                        <h4 key={key} className="font-medium text-brand-secondary mt-2">
                            {line.slice(4)}
                        </h4>
                    );
                }
                if (line.startsWith("- ") || line.startsWith("* ")) {
                    return (
                        <div key={key} className="pl-4 flex gap-2">
                            <span className="text-[var(--brand-accent)]">•</span>
                            <span dangerouslySetInnerHTML={{ __html: sanitize(boldStrong(line.slice(2))) }} />
                        </div>
                    );
                }
                if (line.trim() === "") {
                    return <div key={key} className="h-2" />;
                }
                return (
                    <p
                        key={key}
                        className="text-[var(--text-secondary)] leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: sanitize(boldStrongBrand(line)) }}
                    />
                );
            })}
        </div>
    );
}
